// Shared Canvas2D acoustic charts for Expo web / any DOM host.
// Light-theme ink to match the mobile Graphs tab (not the dark web dock).
//
// This is a thin paint layer: all axis/tick/window/path math comes from
// `geometry.ts`. Skia native is another paint layer over the same geometry —
// both stay pixel-aligned because neither re-derives the log/linear mapping.

import { canvasTypeFont } from '@roomtreatment/design';
import type { PlotFrame } from './layout';
import { FREQ_RESPONSE_PAD, RT60_PAD } from './layout';
import {
  computeAbsorptionGeometry,
  computeFreqResponseGeometry,
  computeRt60Geometry,
  type AxisTicks,
  type YTick,
} from './geometry';
import type { PlotPoint } from './series';

export { magToDb, shiftDbToMean } from './series';

const FR_ACTIVE = '#3A6CA8';
const FR_BASELINE = 'rgba(180, 140, 60, 0.55)';
const RT60_LINE = '#3A6CA8';
const RT60_BASELINE = 'rgba(58, 108, 168, 0.4)';
const TARGET_BAND = 'rgba(46, 125, 50, 0.10)';
const GRID_MAJOR = 'rgba(23, 32, 58, 0.12)';
const GRID_MINOR = 'rgba(23, 32, 58, 0.05)';
const LABEL = '#6B778C';

interface Setup {
  ctx: CanvasRenderingContext2D;
  W: number;
  H: number;
}

function setup(
  canvas: HTMLCanvasElement,
  cssW: number,
  cssH: number,
): Setup | null {
  const W = Math.max(1, Math.floor(cssW));
  const H = Math.max(1, Math.floor(cssH));
  const dpr = typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 2) : 1;
  canvas.width = W * dpr;
  canvas.height = H * dpr;
  canvas.style.width = `${W}px`;
  canvas.style.height = `${H}px`;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, W, H);
  // Set before any geometry call — computeXGeometry's measureWidth callback
  // reads ctx.measureText under the CURRENT font, so it must match what
  // drawGrid/drawYTicks actually render tick labels at.
  ctx.font = canvasTypeFont('tick');
  return { ctx, W, H };
}

function drawGrid(ctx: CanvasRenderingContext2D, frame: PlotFrame, xTicks: AxisTicks) {
  for (const t of xTicks.all) {
    const x = frame.plotX + t.x;
    ctx.strokeStyle = t.major ? GRID_MAJOR : GRID_MINOR;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, frame.plotY);
    ctx.lineTo(x, frame.plotY + frame.plotH);
    ctx.stroke();
  }
  ctx.textAlign = 'center';
  ctx.fillStyle = LABEL;
  for (const t of xTicks.labeled) {
    ctx.fillText(t.label, frame.plotX + t.x, frame.plotY + frame.plotH + 12);
  }
}

function drawYTicks(ctx: CanvasRenderingContext2D, frame: PlotFrame, yTicks: YTick[]) {
  ctx.textAlign = 'right';
  for (const t of yTicks) {
    const y = frame.plotY + t.y;
    ctx.strokeStyle = GRID_MINOR;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(frame.plotX, y);
    ctx.lineTo(frame.plotX + frame.plotW, y);
    ctx.stroke();
    ctx.fillStyle = LABEL;
    ctx.fillText(t.label, frame.plotX - 4, y + 3);
  }
}

function drawLine(
  ctx: CanvasRenderingContext2D,
  frame: PlotFrame,
  pts: PlotPoint[],
  color: string,
  width: number,
) {
  if (pts.length < 2) return;
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(frame.plotX + pts[0]!.x, frame.plotY + pts[0]!.y);
  for (let i = 1; i < pts.length; i++) {
    ctx.lineTo(frame.plotX + pts[i]!.x, frame.plotY + pts[i]!.y);
  }
  ctx.stroke();
}

export interface DrawFreqOpts {
  freqs: number[];
  mag: number[];
  magBaseline?: number[] | null;
  width: number;
  height: number;
  accent?: string;
  fMin?: number;
  fMax?: number;
}

export function drawFreqResponseCanvas(canvas: HTMLCanvasElement, opts: DrawFreqOpts): void {
  const s = setup(canvas, opts.width, opts.height);
  if (!s) return;
  const { ctx, W, H } = s;
  const accent = opts.accent ?? FR_ACTIVE;

  const geo = computeFreqResponseGeometry({
    freqs: opts.freqs,
    mag: opts.mag,
    magBaseline: opts.magBaseline,
    width: opts.width,
    height: opts.height,
    pad: FREQ_RESPONSE_PAD,
    fMin: opts.fMin,
    fMax: opts.fMax,
    measureWidth: (label) => ctx.measureText(label).width,
  });

  if (geo.empty) {
    ctx.fillStyle = LABEL;
    ctx.textAlign = 'center';
    ctx.font = canvasTypeFont('label');
    ctx.fillText('No frequency response data yet', W / 2, H / 2);
    return;
  }

  drawYTicks(ctx, geo.frame, geo.yTicks);
  drawGrid(ctx, geo.frame, geo.xTicks);

  if (geo.baselinePoints) {
    drawLine(ctx, geo.frame, geo.baselinePoints, FR_BASELINE, 1.4);
  }
  drawLine(ctx, geo.frame, geo.activePoints, accent, 1.8);
}

export interface DrawRt60Opts {
  freqs: number[];
  values: number[];
  baselineFreqs?: number[] | null;
  baselineValues?: number[] | null;
  width: number;
  height: number;
  fMin?: number;
  fMax?: number;
}

export function drawRt60Canvas(canvas: HTMLCanvasElement, opts: DrawRt60Opts): void {
  const s = setup(canvas, opts.width, opts.height);
  if (!s) return;
  const { ctx, W, H } = s;

  const geo = computeRt60Geometry({
    freqs: opts.freqs,
    values: opts.values,
    baselineFreqs: opts.baselineFreqs,
    baselineValues: opts.baselineValues,
    width: opts.width,
    height: opts.height,
    pad: RT60_PAD,
    fMin: opts.fMin,
    fMax: opts.fMax,
    measureWidth: (label) => ctx.measureText(label).width,
  });

  const hasPoint = geo.activePoints.length > 0 || (geo.baselinePoints?.length ?? 0) > 0;
  if (geo.empty || !hasPoint) {
    ctx.fillStyle = LABEL;
    ctx.textAlign = 'center';
    ctx.font = canvasTypeFont('label');
    ctx.fillText('No RT60 data yet', W / 2, H / 2);
    return;
  }

  if (geo.targetBandY) {
    ctx.fillStyle = TARGET_BAND;
    ctx.fillRect(geo.frame.plotX, geo.frame.plotY + geo.targetBandY.y0, geo.frame.plotW, geo.targetBandY.y1 - geo.targetBandY.y0);
  }

  drawYTicks(ctx, geo.frame, geo.yTicks);
  drawGrid(ctx, geo.frame, geo.xTicks);

  if (geo.hasBaseline && geo.baselinePoints) {
    drawLine(ctx, geo.frame, geo.baselinePoints, RT60_BASELINE, 1.5);
  }
  drawLine(ctx, geo.frame, geo.activePoints, RT60_LINE, 2.0);
}

export interface DrawAbsorptionOpts {
  freqs: number[];
  alpha: number[];
  width: number;
  height: number;
  /** Omit to auto-fit the axis to `freqs`' actual extent. */
  fMin?: number;
  fMax?: number;
  accent: string;
}

export function drawAbsorptionCanvas(canvas: HTMLCanvasElement, opts: DrawAbsorptionOpts): void {
  const s = setup(canvas, opts.width, opts.height);
  if (!s) return;
  const { ctx } = s;

  const geo = computeAbsorptionGeometry({
    freqs: opts.freqs,
    alpha: opts.alpha,
    width: opts.width,
    height: opts.height,
    fMin: opts.fMin,
    fMax: opts.fMax,
    measureWidth: (label) => ctx.measureText(label).width,
  });

  if (geo.empty) return;

  drawYTicks(ctx, geo.frame, geo.yTicks);
  drawGrid(ctx, geo.frame, geo.xTicks);
  drawLine(ctx, geo.frame, geo.points, opts.accent, 1.8);
}
